<template>
  <v-container fluid>
    <v-row>
      <v-col
        v-for="station in radioStations"
        :key="station.stationuuid"
        cols="12"
        md="4"
        sm="6"
      >
        <v-lazy height="150" max-height="150" min-height="150" :options="{threshold: 0.25}">
          <v-list-item class="align-start bg-grey-darken-4" height="150" rounded @click="onStationClick(station)">
            <v-btn
              class="right-0 top-0 mt-1 mr-1"
              icon
              position="absolute"
              size="x-small"
              variant="text"
              @click.stop="favoriteStore.toggleFavorite(station)"
            >
              <template v-if="favoriteStore.isFavorited(station)">
                <v-icon class="opacity-85" color="red" icon="mdi-heart" size="22" />
              </template>
              <template v-else>
                <v-icon class="opacity-50" icon="mdi-heart-outline" size="22" />
              </template>
            </v-btn>

            <template #prepend>
              <v-avatar class="pa-1" color="grey-darken-3" rounded size="125">
                <v-img
                  v-if="station.favicon"
                  :alt="station.name"
                  :src="station.favicon"
                />
                <div v-else class="bg-grey-darken-2 align-content-center" style="width: 100%; height: 100%;">
                  <v-icon
                    icon="mdi-radio-tower"
                    size="48"
                  />
                </div>
              </v-avatar>
            </template>

            <v-list-item-title class="text-capitalize">
              {{ station.name }}
            </v-list-item-title>

            <v-list-item-subtitle class="text-caption">
              {{ station.state }} {{ station.country }}
            </v-list-item-subtitle>

            <div class="mt-2 overflow-hidden" style="max-height: 72px;">
              <v-chip
                v-for="(tag, index) in getStationTagsArray(station)"
                :key="index"
                class="pa-1 mr-1 mb-1 text-capitalize"
                color="primary"
                label
                size="x-small"
                variant="tonal"
              >
                {{ tag }}
              </v-chip>
            </div>
          </v-list-item>
        </v-lazy>
      </v-col>

      <v-col style="min-height: 300px;">
        <v-bottom-sheet
          :capture-focus="false"
          :model-value="showPlayer"
          persistent
          :scrim="false"
          scroll-strategy="reposition"
        >
          <v-sheet class="pa-2">
            <v-btn
              border="lg"
              icon
              position="absolute"
              size="small"
              style="top: 8px; left: 8px; z-index: 999"
              variant="flat"
              @click.stop="favoriteStore.toggleFavorite(selectedStation)"
            >
              <template v-if="favoriteStore.isFavorited(selectedStation)">
                <v-icon
                  class="opacity-85"
                  color="red"
                  icon="mdi-heart"
                  size="22"
                />
              </template>
              <template v-else>
                <v-icon class="opacity-50" icon="mdi-heart-outline" size="22" />
              </template>
            </v-btn>

            <v-btn
              color="grey-darken-3"
              icon
              position="absolute"
              size="24"
              style="right: 6px; top: 6px; z-index: 999;"
              variant="flat"
              @click="selectedStation = null"
            >
              <v-icon icon="mdi-close" size="16" />
            </v-btn>

            <vuetify-audio-player
              v-if="selectedStation"
              :album-art="selectedStation.favicon"
              :autoplay="autoplay"
              :flat="false"
              :is-favorited="favoriteStore.isFavorited(selectedStation)"
              :minimal="false"
              :src="selectedStation.url_resolved"
              :subtitle="`${selectedStation.state} ${selectedStation.country}`"
              :tags="getStationTagsArray(selectedStation)"
              :title="selectedStation.name"
              :visualizer="true"
            />
          </v-sheet>
        </v-bottom-sheet>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import { mapStores, mapWritableState } from 'pinia'
  import VuetifyAudioPlayer from '@/components/VuetifyAudioPlayer.vue'
  import { useAppStore } from '@/stores/appStore'
  import { useFavoriteStore } from '@/stores/favoriteStore'
  import { useRadioStore } from '@/stores/radioStore'

  export default {
    name      : 'Home',
    components: { VuetifyAudioPlayer },
    props     : {},
    data () {
      return {
        autoplay: false
      }
    },
    computed: {
      ...mapStores(useFavoriteStore),
      ...mapStores(useRadioStore),
      ...mapStores(useAppStore),
      ...mapWritableState(useRadioStore, ['selectedStation']),

      radioStations () {
        const favoritesFilterActive = this.appStore.favoritesModel
        const searchFilterActive = !!this.appStore.searchModel
        const searchTerms = searchFilterActive ? this.appStore.searchModel.toLocaleLowerCase(this.$i18n.locale) : ''
        const compareFunc = (a, b, desc) => desc ? (b - a || String(b).localeCompare(a)) : (a - b || String(a).localeCompare(b))
        const groupBy = (x, f) => x.reduce((a, b, i) => ((a[f(b, i, x)] ||= []).push(b), a), {})

        const radios = this.radioStore.stations
          .filter(station => favoritesFilterActive ? this.favoriteStore.isFavorited(station) : true)
          .filter(station => searchFilterActive ? station.name.toLocaleLowerCase(this.$i18n.locale).includes(searchTerms) || station.state.toLocaleLowerCase(this.$i18n.locale).includes(searchTerms) : true)

        return radios.toSorted((a, b) => compareFunc(a[this.appStore.sortModel[0]], b[this.appStore.sortModel[0]], this.appStore.sortType === 'desc'))
      },

      showPlayer () {
        return !!this.selectedStation
      }
    },
    watch: {
      radioStations () {
        window.scrollTo(0, 0)
      }
    },
    created () {
      this.radioStore.getByCountry()
    },
    mounted () {},
    beforeUnmount () {},
    unmounted () {},
    methods: {
      onStationClick (station) {
        this.autoplay = true
        this.selectedStation = station.stationuuid === this.selectedStation?.stationuuid ? null : station
      },

      getStationTagsArray (station) {
        return station?.tags?.length > 0 ? station.tags.split(',').slice(0, 10) : []
      }
    }

  }
</script>

<style lang="css" scoped>
:deep(.v-list-item__append) {
  align-self: start;
}
</style>
